import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import { Theme } from '@mui/material/styles';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import { useAppSelector } from '../../../store/hooks';

export default function OwnerDialogSelect({
    handleSwapOwner,
    showDialog,
    setShowDialog,
    handleShowDialog,
    prevOwner,
    ...props
}: Props) {
    const { classes } = useStyles();

    const users = useAppSelector(state => state.user.users);

    const sortedUsers = React.useMemo(() => {
        return [...users].sort((a, b) => {
            const userNameA = a.userName.toUpperCase();
            const userNameB = b.userName.toUpperCase();
            return userNameA < userNameB ? -1 : userNameA > userNameB ? 1 : 0;
        });
    }, [users]);

    const [text, setText] = React.useState<string | undefined>(prevOwner);

    const handleConfirm = () => {
        handleSwapOwner(text);
    };

    const handleCloseDialog = () => {
        setShowDialog(false);
    };

    return (
        <>
            <AddCircleOutlineIcon
                className={classes.addOwnerBtn}
                onClick={handleShowDialog}
                color="action"
            />
            <Dialog
                open={showDialog}
                onClose={handleCloseDialog}
                classes={{ paper: classes.dialogPaper }}
            >
                <DialogTitle classes={{ root: classes.dialogTitle }}>Select Owner</DialogTitle>
                <DialogContent classes={{ root: classes.dialogContent }}>
                    <Box component="form" sx={{ display: 'flex', flexWrap: 'wrap' }}>
                        <TextField
                            className={classes.selectWrapper}
                            {...props}
                            select
                            value={text}
                            id={props.id || 'select_user'}
                            label={props.label || 'Select User'}
                            onChange={event => setText(event.target.value)}
                            SelectProps={{
                                MenuProps: {
                                    PaperProps: {
                                        className: classes.paper,
                                    },
                                },
                            }}
                        >
                            {sortedUsers.map(user => (
                                <MenuItem
                                    key={user.userName}
                                    value={user.userName}
                                    className={classes.menuItem}
                                >
                                    {user.userName}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleConfirm}>Ok</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

const useStyles = makeStyles()((theme: Theme) => ({
    addOwnerBtn: {
        cursor: 'pointer',
    },
    selectWrapper: {
        display: 'flex',
        justifyContent: 'flex-end',
        margin: theme.spacing(2, 0),
        minWidth: '20rem',
    },
    paper: {
        maxHeight: '200px',
    },
    menuItem: {
        '&:hover': {
            backgroundColor: theme.palette.menuListHover.light,
        },
        '&.Mui-selected': {
            backgroundColor: theme.palette.menuListHover.main,
            '&:hover': {
                backgroundColor: theme.palette.menuListHover.main,
            },
        },
    },
    dialogPaper: {
        minHeight: '25rem',
        maxHeight: '25rem',
    },
    dialogTitle: {
        minWidth: '24rem',
    },
    dialogContent: {
        overflowY: 'hidden',
    },
}));

type Props = TextFieldProps & {
    handleSwapOwner: (text: string | undefined) => void;
    showDialog: boolean;
    setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
    handleShowDialog: () => void;
    prevOwner: string | undefined;
};
